import React, { useMemo, useState } from 'react';
import { FunctionUtilization } from '@hud/portal-dtos/webapp-function.dto';
import {
  PaginationState,
  SortingState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { DefaultTable, LinkCell } from '@hud/web-components/Table/Table';
import { isUnnamed } from '../../utils';
import HoverPopover from '@hud/web-components/HoverPopover/HoverPopover';
import { useGlobalQueryString } from '../../hooks/useGlobalFilter';
import LongContentPopover from '@hud/web-components/LongContentPopover/LongContentPopover';
import { FilePath, FunctionPopover, Signature } from './DeadCodeTable.styles';
import { APILabel } from '../../components/endpointsCellRenderer/endpointsCellRenderer.styles';
import { renderDatetime } from '@hud/web-components/Table/CellRenderers';
import { DEFAULT_PAGE_SIZE } from '../../consts';
import TableContainer from '@hud/web-components/Container/TableContainer';
import { Search } from '@hud/web-components/Search/Search';
import { HorizontalContainer } from '@hud/web-components/Container/HorizontalContainer';
import Pagination from '@hud/web-components/Pagination/Pagination';

export interface DeadCodeTableProps {
  functions: FunctionUtilization[];
  loading: boolean;
}

const columnHelper = createColumnHelper<FunctionUtilization>();

const DeadCodeTable: React.FC<DeadCodeTableProps> = ({
  functions,
  loading,
}) => {
  const globalFilters = useGlobalQueryString();
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'linesOfCode', desc: true },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const deadCodeFunctions = useMemo(() => {
    // filter out functions that have been invoked, sort by lines of code  and show only top 50
    return functions
      .filter((func) => func.normalizedRuns === 0)
      .sort((a, b) => b.linesOfCode - a.linesOfCode)
      .slice(0, 50);
  }, [functions]);

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('signature', {
        header: 'Function',
        cell: (cell) => (
          <LinkCell
            to={`/explore/functions/${cell.row.original.id}?${globalFilters}`}
          >
            <HoverPopover
              body={
                <FunctionPopover>
                  <Signature>{cell.getValue()}</Signature>
                  <FilePath>
                    <span>File path: </span>
                    <span>
                      {cell.row.original.filePath} {/*todo: add line number*/}
                      {/* : */}
                      {/* {cell.row.original.lineNumber} */}
                    </span>
                  </FilePath>
                </FunctionPopover>
              }
            >
              {isUnnamed(cell.getValue()) &&
              cell.row.original.totalEndpoints === 1 ? (
                <APILabel>
                  {cell.row.original.endpoints[0].method.toLocaleUpperCase() +
                    ' ' +
                    cell.row.original.endpoints[0].path}
                </APILabel>
              ) : (
                cell.getValue()
              )}
            </HoverPopover>
          </LinkCell>
        ),
        meta: {
          copyable: true,
          copyableItems: [
            {
              label: 'signature',
              accessor: (row: FunctionUtilization) => row.signature,
              icon: 'other.codeBrackets',
            },
            {
              label: 'path',
              accessor: (row: FunctionUtilization) => row.filePath,
              leftSection: '/...',
            },
          ],
        },
        minSize: 150,
        size: 250,
      }),
      columnHelper.accessor('filePath', {
        header: 'Path',
        cell: (cell) => (
          <LongContentPopover reverseEllipsis={true}>
            {cell.getValue()}
          </LongContentPopover>
        ),
        meta: { copyable: true },
      }),
      columnHelper.accessor('linesOfCode', {
        header: 'Lines of code',
        cell: (cell) => (
          <LongContentPopover>{cell.getValue()}</LongContentPopover>
        ),
        size: 80,
      }),
      columnHelper.accessor('lastChangeAt', {
        header: 'Last change',
        cell: renderDatetime(true),
        size: 160,
      }),
    ];
  }, [globalFilters]);

  const table = useReactTable({
    data: deadCodeFunctions,

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    columnResizeMode: 'onChange',
    columns,
    state: {
      pagination,
      sorting,
      globalFilter,
    },
  });

  return (
    <TableContainer.Container title={'Dead Code'}>
      <TableContainer.Header>
        <Search
          inline
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
      </TableContainer.Header>
      <HorizontalContainer>
        <DefaultTable
          table={table}
          label="Dead Code"
          loading={loading}
          loadingRows={5}
        />
      </HorizontalContainer>
      <TableContainer.Footer>
        <Pagination
          pages={loading ? 0 : table.getPageCount()}
          currentPage={
            loading || table.getPageCount() === 0
              ? 0
              : table.getState().pagination.pageIndex + 1
          }
          onPageChange={(page) => table.setPageIndex(page - 1)}
          loading={loading}
        />
      </TableContainer.Footer>
    </TableContainer.Container>
  );
};

export default DeadCodeTable;
